import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';
import { getTaxonomyFromS3 } from '../services/s3Service';
import { downloadFilingPDF } from '../services/pdfService';
import { useCompany } from '../contexts/CompanyContext';
import { findNegativeWeightTargets } from '../utils/findNegativeTargets';
import PDFViewer from './PDFViewer';
import {
  PDF_VIEWER_CHANGED_EVENT,
  setPdfViewerOpen,
  togglePdfViewerOpen,
} from '../utils/pdfViewerBus';
import {
  compareReportNoteSortKeys,
  extractReportNoteLabel,
  getReportNoteSortKeyFromHtml,
  isNotePolicyOrOtherSection,
} from '../utils/reportNoteOrder';
import '../App.css';
import '../styles/main-layout.css';
import '../styles/view-filing.css';

interface Column {
  label: string;
  member: string;
  period: 'current' | 'comparative';
}

export function ViewFilings() {
  const { filingId } = useParams();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  
  // Data refs (no re-renders needed for viewing)
  const formStructureRef = useRef<any>(null);
  const companyInfoRef = useRef<any>(null);
  const formDataRef = useRef<Record<string, Record<string, any>>>({});
  const negativeTargetsRef = useRef<string[]>([]);
  
  // Global footnote registry (shared across ALL sections)
  const globalFootnoteRegistryRef = useRef<Map<string, { number: number; text: string }>>(new Map());
  
  // Scroll container ref for position restoration
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Side-by-side PDF layout refs (DOM-driven, matches EditFilings)
  const mainLayoutRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const rightAreaRef = useRef<HTMLDivElement>(null);
  const pdfColumnRef = useRef<HTMLDivElement>(null);
  const compactNavRef = useRef<HTMLDivElement>(null);
  const isPdfViewerOpenRef = useRef(false);

  // Lock to prevent Intersection Observer from fighting manual navigation
  const manualScrollLockRef = useRef<NodeJS.Timeout | null>(null);
  
  // Minimal state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const [columnsHidden, setColumnsHidden] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filingOrgId, setFilingOrgId] = useState<string | null>(null);
  /** OFF = taxonomy order; ON = sort Note Policy / Note Other by report note no. (6, 5.4, (a)…) */
  const [sortByNoteNo, setSortByNoteNo] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Rapid Tagger | View Filing';
    return () => {
      document.title = 'Rapid Tagger';
      // Cleanup manual scroll lock timeout
      if (manualScrollLockRef.current) {
        clearTimeout(manualScrollLockRef.current);
      }
      // Ensure PDF mode is closed when leaving view page
      setPdfViewerOpen(false);
    };
  }, []);

  const applyPdfOpenToDOM = useCallback((open: boolean) => {
    isPdfViewerOpenRef.current = open;

    if (mainLayoutRef.current) {
      mainLayoutRef.current.classList.toggle('with-pdf-viewer', open);
    }
    if (rightAreaRef.current) {
      rightAreaRef.current.classList.toggle('with-pdf-split', open);
    }
    if (sidebarRef.current) {
      sidebarRef.current.style.display = open ? 'none' : '';
    }
    if (compactNavRef.current) {
      compactNavRef.current.style.display = open ? 'flex' : 'none';
    }
    if (pdfColumnRef.current) {
      pdfColumnRef.current.classList.remove('pdf-viewer-column', 'pdf-viewer-hidden-container');
      pdfColumnRef.current.classList.add(open ? 'pdf-viewer-column' : 'pdf-viewer-hidden-container');
    }
  }, []);

  useEffect(() => {
    setPdfViewerOpen(false);

    const handler = (e: any) => {
      applyPdfOpenToDOM(!!e?.detail?.isOpen);
    };

    document.addEventListener(PDF_VIEWER_CHANGED_EVENT, handler);
    applyPdfOpenToDOM(false);

    return () => document.removeEventListener(PDF_VIEWER_CHANGED_EVENT, handler);
  }, [applyPdfOpenToDOM]);

  const handleComparePDF = useCallback(() => {
    togglePdfViewerOpen();
  }, []);

  const scrollToSection = useCallback((sectionCode: string) => {
    if (manualScrollLockRef.current) {
      clearTimeout(manualScrollLockRef.current);
    }
    setActiveSectionId(sectionCode);
    document.getElementById(sectionCode)?.scrollIntoView({ behavior: 'smooth' });
    manualScrollLockRef.current = setTimeout(() => {
      manualScrollLockRef.current = null;
    }, 1000);
  }, []);
  
  // Load filing data (used by both initial load and refresh)
  const loadFilingData = useCallback(async (preserveScroll = false) => {
    if (!filingId) {
      setError('No filing ID provided');
      setLoading(false);
      return;
    }

    try {
      console.log('📥 Loading filing for view:', filingId);

      // Save scroll position if refreshing
      const savedScrollTop = preserveScroll && scrollContainerRef.current 
        ? scrollContainerRef.current.scrollTop 
        : 0;

      // 1. Get ALL filing data in ONE query (company_info, org_id, form_data)
      const { data: filingData, error: filingError } = await supabase
        .from('filings')
        .select('company_info, org_id, form_data')
        .eq('id', filingId)
        .single();

      if (filingError || !filingData) {
        throw new Error('Filing not found');
      }

      companyInfoRef.current = filingData.company_info;
      setFilingOrgId(filingData.org_id || null);

      // 2. Load taxonomy from S3
      const taxonomy = await getTaxonomyFromS3(filingData.org_id, filingId);
      formStructureRef.current = taxonomy;
      
      // Find negative weight targets
      const negativeTargets = findNegativeWeightTargets(taxonomy);
      negativeTargetsRef.current = negativeTargets;
      console.log('✅ Found negative targets:', negativeTargets);

      // 3. Form data already loaded in initial query
      if (filingData.form_data) {
        formDataRef.current = filingData.form_data;
        console.log('✅ Form data loaded:', Object.keys(formDataRef.current).length, 'contexts');
        console.log('All contexts:', Object.keys(formDataRef.current));
        
        // Show sample data structure
        const sampleContext = Object.keys(formDataRef.current)[0];
        if (sampleContext) {
          console.log('Sample data structure:', {
            context: sampleContext,
            fields: Object.keys(formDataRef.current[sampleContext]).slice(0, 3)
          });
        }
      }

      // Set first section as active (only on initial load)
      if (!preserveScroll && taxonomy?.sections) {
        const firstSection = Object.keys(taxonomy.sections)[0];
        setActiveSectionId(firstSection);
      }

      console.log('✅ View data loaded successfully');
      setLoading(false);
      setRefreshing(false);

      // Restore scroll position if refreshing
      if (preserveScroll && scrollContainerRef.current) {
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = savedScrollTop;
            console.log('✅ Scroll position preserved:', savedScrollTop);
          }
        }, 100);
      }

    } catch (err: any) {
      console.error('Error loading view:', err);
      setError(err.message || 'Failed to load filing');
      setLoading(false);
      setRefreshing(false);
    }
  }, [filingId]);

  // Initial load
  useEffect(() => {
    loadFilingData(false);
  }, [loadFilingData]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    setColumnsHidden(false); // Reset column hiding
    await loadFilingData(true); // Preserve scroll position
  };

  // 🔒 FEATURE: Auto-switch active tab based on scroll position (like MBRS 1.0)
  useEffect(() => {
    if (loading || !formStructureRef.current) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Get all section elements (section IDs are like "100100", "200100", etc.)
    const sectionElements = Array.from(
      scrollContainer.querySelectorAll('.view-section[id]')
    ) as HTMLElement[];

    if (sectionElements.length === 0) return;

    // Intersection Observer options
    const options = {
      root: scrollContainer,
      rootMargin: '-20% 0px -70% 0px', // Top 20%, bottom 70% - prioritizes top sections
      threshold: 0
    };

    // Track which sections are visible
    const visibleSections = new Map<string, number>();

    const observer = new IntersectionObserver((entries) => {
      // 🔒 Don't update if user manually clicked a tab (prevent flickering)
      if (manualScrollLockRef.current !== null) {
        return;
      }

      entries.forEach(entry => {
        const sectionId = entry.target.id;
        if (entry.isIntersecting) {
          visibleSections.set(sectionId, entry.intersectionRatio);
        } else {
          visibleSections.delete(sectionId);
        }
      });

      // Find the most visible section (highest in the viewport)
      if (visibleSections.size > 0) {
        const firstVisibleSection = sectionElements.find(el => 
          visibleSections.has(el.id)
        );
        if (firstVisibleSection && firstVisibleSection.id !== activeSectionId) {
          setActiveSectionId(firstVisibleSection.id);
        }
      }
    }, options);

    // Observe all sections
    sectionElements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [loading, activeSectionId]);

  // Hide empty columns in SOCIE/Note tables (matches 1.0 lines 1488-1552)
  useEffect(() => {
    if (loading || columnsHidden) return;
    
    // Wait for tables to render
    setTimeout(() => {
      const isEmptyOrZero = (value: string) => {
        if (!value) return true;
        value = value.trim();
        return value === '' || value === '0' || value === '0.00' || value === '-';
      };
      
      // SOCIE / notes / PPE wide tables — not SOFP/SOPL
      const socieAndNoteTables = document.querySelectorAll('.section-wide-table .financial-table');
      
      socieAndNoteTables.forEach((table) => {
        const headers = table.querySelectorAll('thead th');
        const rows = table.querySelectorAll('tbody tr');
        
        // 🔒 CRITICAL FIX: Reset all columns to visible and store original colspans before hiding
        // This prevents colspan from being reduced multiple times on refresh
        const originalColspans = new Map<Element, string>();
        
        // Reset all header columns to visible
        headers.forEach((header) => {
          (header as HTMLElement).style.display = '';
        });
        
        // Reset all body cells to visible and store original colspans
        rows.forEach((row) => {
          const cells = row.querySelectorAll('td');
          cells.forEach((cell) => {
            (cell as HTMLElement).style.display = '';
          });
          
          // Store original colspan values
          const spanCell = row.querySelector('td[colspan]');
          if (spanCell) {
            const originalColspan = spanCell.getAttribute('data-original-colspan');
            if (!originalColspan) {
              // First time - store the original colspan
              const currentColspan = spanCell.getAttribute('colspan') || '';
              spanCell.setAttribute('data-original-colspan', currentColspan);
              originalColspans.set(spanCell, currentColspan);
            } else {
              // Restore from stored original
              spanCell.setAttribute('colspan', originalColspan);
              originalColspans.set(spanCell, originalColspan);
            }
          }
        });
        
        // Now hide empty columns
        // Skip first column (component names) and last column (total)
        for (let colIndex = 1; colIndex < headers.length - 1; colIndex++) {
          let hasValue = false;
          
          // Check all rows for this column
          rows.forEach((row) => {
            // Skip header rows with colspan
            if (row.querySelector('td[colspan]')) return;
            
            const cells = row.querySelectorAll('td');
            if (cells.length > colIndex) {
              const cellValue = cells[colIndex].textContent || '';
              if (!isEmptyOrZero(cellValue)) {
                hasValue = true;
              }
            }
          });
          
          // If column is empty, hide it
          if (!hasValue) {
            // Hide the header
            (headers[colIndex] as HTMLElement).style.display = 'none';
            
            // Hide the cell in each row
            rows.forEach((row) => {
              const cells = row.querySelectorAll('td');
              if (cells.length > colIndex && !row.querySelector('td[colspan]')) {
                (cells[colIndex] as HTMLElement).style.display = 'none';
              }
            });
            
            // Adjust colspan for header rows (using stored original values)
            rows.forEach((row) => {
              const spanCell = row.querySelector('td[colspan]');
              if (spanCell) {
                const originalColspan = parseInt(originalColspans.get(spanCell) || '0');
                if (!isNaN(originalColspan) && originalColspan > 0) {
                  spanCell.setAttribute('colspan', (originalColspan - 1).toString());
                }
              }
            });
          }
        }
      });

      // Decide print orientation from VISIBLE columns only (same rule as Lambda).
      // SOCIE / 740000-750000 note tables: 8+ visible cols → landscape.
      // Note Other / Policy: each disclosure text-block is classified separately.
      const LANDSCAPE_MIN_VISIBLE_COLS = 8;
      const visibleColsInTable = (table: Element) => {
        let max = 0;
        table.querySelectorAll('tr').forEach((row) => {
          const cells = Array.from(row.children).filter((cell) => {
            const el = cell as HTMLElement;
            return el.style.display !== 'none' && el.style.visibility !== 'hidden';
          });
          if (!cells.length) return;
          if (cells.length === 1) {
            const span = parseInt(cells[0].getAttribute('colspan') || '1', 10) || 1;
            if (span > 1) return;
          }
          const cols = cells.reduce((n, cell) => {
            return n + (parseInt(cell.getAttribute('colspan') || '1', 10) || 1);
          }, 0);
          if (cols > max) max = cols;
        });
        return max;
      };

      document.querySelectorAll('.section-wide-table.view-section').forEach((section) => {
        const el = section as HTMLElement;
        const isPpeOrIa = /PropertyPlantAndEquipment|IntangibleAssets/.test(el.id || '');
        if (isPpeOrIa) {
          el.classList.remove('section-landscape');
          el.classList.add('section-fit-width');
          return;
        }

        let maxVisible = 0;
        el.querySelectorAll('table').forEach((table) => {
          maxVisible = Math.max(maxVisible, visibleColsInTable(table));
        });

        el.classList.toggle('section-landscape', maxVisible >= LANDSCAPE_MIN_VISIBLE_COLS);
      });

      // Note Other / Accounting Policy: landscape per disclosure block only
      // (e.g. "Disclosure of basis of preparation..."), not the whole 730000 section.
      document.querySelectorAll('.view-section').forEach((section) => {
        const sectionId = (section as HTMLElement).id || '';
        if (!sectionId.startsWith('730000_') && !sectionId.startsWith('720000_')) return;

        section.querySelectorAll('.text-block-container').forEach((block) => {
          const el = block as HTMLElement;
          let maxVisible = 0;
          el.querySelectorAll('table').forEach((table) => {
            maxVisible = Math.max(maxVisible, visibleColsInTable(table));
          });
          el.classList.toggle('section-landscape', maxVisible >= LANDSCAPE_MIN_VISIBLE_COLS);
        });
      });
      
      setColumnsHidden(true);
      console.log('✅ Empty columns hidden; landscape class applied from visible column count');
    }, 500);
  }, [loading, columnsHidden]);

  // ✅ FIX: Sanitize HTML to remove TinyMCE artifacts and dangerous elements
  const sanitizeHTML = (html: string): string => {
    if (!html) return '';
    
    // Remove TinyMCE blocker divs (these block the entire page!)
    html = html.replace(/<div[^>]*class="ephox-dragster-blocker"[^>]*>.*?<\/div>/gi, '');
    
    // Remove other TinyMCE internal elements
    html = html.replace(/<div[^>]*class="mce-[^"]*"[^>]*>.*?<\/div>/gi, '');
    html = html.replace(/<div[^>]*data-mce-[^>]*>.*?<\/div>/gi, '');
    
    // Remove any fixed position divs that could block the page
    html = html.replace(/<div[^>]*position:\s*fixed[^>]*>.*?<\/div>/gi, '');
    
    return html;
  };

  // Get value from saved data (matches database structure)
  const getValue = (qname: string, contextRef: string, footnoteRegistry?: Map<string, any>): string => {
    if (!contextRef || !qname) return '';
    
    const contextData = formDataRef.current[contextRef];
    if (!contextData) return '';
    
    const fieldData = contextData[qname];
    if (!fieldData) return '';
    
    let value = fieldData.value || '';
    
    // ✅ FIX: Sanitize HTML content (remove TinyMCE blockers and artifacts)
    if (value && typeof value === 'string' && value.includes('<')) {
      value = sanitizeHTML(value);
    }
    
    // Format monetary values and shares (thousand separator)
    const dataType = fieldData.metadata?.type ?? fieldData.metadata?.data_type;
    if ((dataType === 'monetary' || dataType === 'shares') && value) {
      try {
        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        const isMonetary = dataType === 'monetary';
        value = numValue.toLocaleString('en-US', {
          minimumFractionDigits: isMonetary ? 2 : 0,
          maximumFractionDigits: isMonetary ? 2 : 0
        });
      } catch {
        // Keep original value if formatting fails
      }
    }
    
    // Handle footnotes (numbered sequentially within each section)
    if (footnoteRegistry && fieldData.metadata?.footnote) {
      const footnoteKey = `${qname}_${contextRef}`;
      if (!footnoteRegistry.has(footnoteKey)) {
        // Number footnotes sequentially: 1, 2, 3... within this section
        const footnoteNumber = footnoteRegistry.size + 1;
        footnoteRegistry.set(footnoteKey, {
          number: footnoteNumber,
          text: fieldData.metadata.footnote
        });
      }
      const footnoteNum = footnoteRegistry.get(footnoteKey).number;
      return `<span class="footnote-inline">[${footnoteNum}]</span> ${value}`;
    }
    
    return value;
  };

  // Build columns based on company info (matches 1.0 logic)
  const buildColumns = (): Column[] => {
    const columns: Column[] = [];
    const companyInfo = companyInfoRef.current;
    
    if (!companyInfo) return columns;
    
    if (companyInfo.consolidated === 'Y') {
      columns.push({
        label: 'Current Year Consolidated',
        member: '_ConsolidatedMember',
        period: 'current'
      });
      
      if (companyInfo.first_year === 'N') {
        columns.push({
          label: 'Previous Year Consolidated',
          member: '_ConsolidatedMember',
          period: 'comparative'
        });
      }
      
      columns.push({
        label: 'Current Year Separate',
        member: '_SeparateMember',
        period: 'current'
      });
      
      if (companyInfo.first_year === 'N') {
        columns.push({
          label: 'Previous Year Separate',
          member: '_SeparateMember',
          period: 'comparative'
        });
      }
    } else {
      columns.push({
        label: 'Current Year',
        member: '_SeparateMember',
        period: 'current'
      });
      
      if (companyInfo.first_year === 'N') {
        columns.push({
          label: 'Previous Year',
          member: '_SeparateMember',
          period: 'comparative'
        });
      }
    }
    
    return columns;
  };

  // Get context ref for financial tables (includes member dimension)
  const getContextRef = (item: any, column: Column): string => {
    const companyInfo = companyInfoRef.current;
    if (!companyInfo) {
      console.error('No company info available');
      return '';
    }
    
    const period = column.period === 'current' ? companyInfo.currentPeriod : companyInfo.previousPeriod;
    if (!period) {
      console.error('No period info');
      return '';
    }
    
    const periodType = item.properties?.period_type;
    
    // Clean member (remove leading underscore)
    const cleanMember = column.member.startsWith('_') ? column.member.substring(1) : column.member;
    
    if (periodType === 'duration') {
      const start = period.start?.replace(/-/g, '') || '';
      const end = period.end?.replace(/-/g, '') || '';
      return `fromto_${start}_${end}_${cleanMember}`;
    } else {
      const end = period.end?.replace(/-/g, '') || '';
      return `asof_${end}_${cleanMember}`;
    }
  };

  const handlePrint = async () => {
    if (!filingId) return;
    
    try {
      setGeneratingPDF(true);
      
      // Extract company info for filename generation (matches XBRL export pattern)
      const formData = formDataRef.current;
      let submission_type = 'FS-MPERS'; // Default
      let company_number = 'UNKNOWN';
      let reporting_end = '';
      
      // Get submission type (support MY CA2016/CA1965 and SG prefixes)
      for (const contextFacts of Object.values(formData)) {
        if (contextFacts['ssmt-dei:TypeOfSubmission']) {
          submission_type = contextFacts['ssmt-dei:TypeOfSubmission'].value;
          break;
        }
        if (contextFacts['ssmt-dei-1965:TypeOfSubmission']) {
          submission_type = contextFacts['ssmt-dei-1965:TypeOfSubmission'].value;
          break;
        }
        if (contextFacts['sg-dei:TypeOfXBRLFiling']) {
          submission_type = contextFacts['sg-dei:TypeOfXBRLFiling'].value;
          break;
        }
      }
      
      // Get entity registration number (support MY CA2016/CA1965 and SG prefixes)
      for (const contextFacts of Object.values(formData)) {
        if (contextFacts['ssmt-dei:NewCompanyRegistrationNumber']) {
          company_number = contextFacts['ssmt-dei:NewCompanyRegistrationNumber'].value.replace(/-/g, '');
          break;
        }
        if (contextFacts['ssmt-dei-1965:NewCompanyRegistrationNumber']) {
          company_number = contextFacts['ssmt-dei-1965:NewCompanyRegistrationNumber'].value.replace(/-/g, '');
          break;
        }
        if (contextFacts['sg-dei:UniqueEntityNumber']) {
          company_number = contextFacts['sg-dei:UniqueEntityNumber'].value.replace(/-/g, '');
          break;
        }
      }
      
      // Get reporting period end date
      const companyInfo = companyInfoRef.current;
      if (companyInfo?.currentPeriod?.end) {
        reporting_end = companyInfo.currentPeriod.end.replace(/-/g, '');
      }
      
      await downloadFilingPDF(filingId, {
        submission_type,
        company_number,
        reporting_end
      });
    } catch (err: any) {
      console.error('PDF generation failed:', err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading filing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-500 text-white rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Section configuration (matches 1.0 application_filing.py lines 919-1095)
  const sectionConfig: Record<string, { display_name: string; category: string }> = {
    '010000_FilingInformationAbstract': { display_name: 'Filing Information', category: 'Disclosure' },
    '020000_DisclosureOnScopeOfFilingAbstract': { display_name: 'Scope Of Filing', category: 'Disclosure' },
    '120000_DisclosureOnDirectorsReportAbstract': { display_name: 'Directors Report', category: 'Disclosure' },
    '120100_DisclosureOnStatementByDirectorsAbstract': { display_name: 'Directors Statement', category: 'Disclosure' },
    '120200_DisclosureOnDirectorsBusinessReviewAbstract': { display_name: 'Business Review', category: 'Disclosure' },
    '110000_DisclosureOnInvolvementInStockExchangeAbstract': { display_name: 'Involvement Stock Exchange', category: 'Disclosure' },
    '130000_DisclosureOnAuditorsReportToMembersAbstract': { display_name: 'Auditors Report', category: 'Disclosure' },
    '200000_DisclosureOfFullSetOfFinancialStatementsAbstract': { display_name: 'Full Set of Financial Statements', category: 'Disclosure' },
    '200100x_DisclosureOnStatementOfFinancialPositionAbstract': { display_name: 'SOFP CuNonCu', category: 'Statements' },
    '200100y_DisclosureOnStatementOfFinancialPositionAbstract': { display_name: 'SOFP Liquidity', category: 'Statements' },
    '200200x_DisclosureOnSubclassificationOfAssetsLiabilitiesAndEquityAbstract': { display_name: 'SOFP CuNonCu - Details', category: 'Statements' },
    '200200y_DisclosureOnSubclassificationOfAssetsLiabilitiesAndEquityAbstract': { display_name: 'SOFP Liquidity - Details', category: 'Statements' },
    '300100x_DisclosureOnStatementOfProfitOrLossAbstract': { display_name: 'SOPL Function', category: 'Statements' },
    '300100y_DisclosureOnStatementOfProfitOrLossAbstract': { display_name: 'SOPL Nature', category: 'Statements' },
    '300200x_DisclosureOnStatementOfProfitOrLossAbstract': { display_name: 'SOPL Function - Details', category: 'Statements' },
    '300200y_DisclosureOnStatementOfProfitOrLossAbstract': { display_name: 'SOPL Nature - Details', category: 'Statements' },
    '400100x_DisclosureOnStatementOfComprehensiveIncomeNetOfTaxAbstract': { display_name: 'SOCI - After Tax', category: 'Statements' },
    '400100y_DisclosureOnStatementOfComprehensiveIncomeBeforeTax': { display_name: 'SOCI - Before Tax', category: 'Statements' },
    '500100y_DisclosureOnStatementOfCashFlowsAbstract': { display_name: 'Cash Flow - Indirect', category: 'Statements' },
    '500100x_DisclosureOnStatementOfCashFlowsAbstract': { display_name: 'Cash Flow - Direct', category: 'Statements' },
    '610000_DisclosureOfChangesInEquityAbstract': { display_name: 'SOCIE', category: 'Statements' },
    '620000_DisclosureOfRetainedEarningsAbstract': { display_name: 'SORE', category: 'Statements' },
    '710000_DisclosureOnCorporateInformationAbstract': { display_name: 'Note - Corporate Info', category: 'Notes' },
    '720000_DisclosureOnSummaryOfSignificantAccountingPoliciesAbstract': { display_name: 'Note - Accounting Policy', category: 'Notes' },
    '720000_DisclosureOnSummaryOfMaterialAccountingPoliciesAbstract': { display_name: 'Note - Accounting Policy', category: 'Notes' },
    '730000_DisclosureOfNotesAndOtherExplanatoryInformationAbstract': { display_name: 'Note - Other', category: 'Notes' },
    '740000_DisclosureOnIssuedCapitalAbstract': { display_name: 'Note - Issued Capital', category: 'Notes' },
    '750000_DisclosureOnRelatedPartyTransactionsAbstract': { display_name: 'Note - Related Party', category: 'Notes' },
    '300100x_DisclosureOnStatementOfIncomeAndExpenditureAbstract': { display_name: 'SOIE Function', category: 'Statements' },
    '300100y_DisclosureOnStatementOfIncomeAndExpenditureAbstract': { display_name: 'SOIE Nature', category: 'Statements' },
    '300200x_DisclosureOnAnalysisOfIncomeAndExpenditureAbstract': { display_name: 'SOIE Function - Details', category: 'Statements' },
    '300200y_DisclosureOnAnalysisOfIncomeAndExpenditureAbstract': { display_name: 'SOIE Nature - Details', category: 'Statements' },
    '610000_DisclosureOnStatementOfChangesInFundAbstract': { display_name: 'SOCIE', category: 'Statements' },

    // Malaysia CLBG/CLBS 1965 sections
    '130000_AuditorsReportAbstract': { display_name: 'Auditors Report', category: 'Disclosure' },
    '210000_DisclosureOnStatementOfFinancialPositionAbstract': { display_name: 'SOFP CuNonCu', category: 'Statements' },
    '220000_DisclosureOnStatementOfFinancialPositionAbstract': { display_name: 'SOFP Liquidity', category: 'Statements' },
    '310000_DisclosureOnStatementOfProfitOrLossAbstract': { display_name: 'SOPL', category: 'Statements' },
    '210000_StatementOfFinancialPositionAbstract': { display_name: 'SOFP CuNonCu', category: 'Statements' },
    '220000_StatementOfFinancialPositionAbstract': { display_name: 'SOFP Liquidity', category: 'Statements' },
    '310000_StatementOfProfitOrLossAbstract': { display_name: 'SOPL', category: 'Statements' },


    // Singapore ACRA sections
    '10000000_DisclosureOfFilingInformationAbstract': { display_name: 'Filing Information', category: 'Disclosure' },
    '11000000_DisclosureOfCompleteSetOfFinancialStatementsAbstract': { display_name: 'Full Set Statements', category: 'Disclosure' },
    '12000000_DisclosureInStatementByDirectorsAbstract': { display_name: 'Directors Statement', category: 'Disclosure' },
    '13000000_DisclosuresInIndependentAuditorsReportAbstract': { display_name: 'Auditors Report', category: 'Disclosure' },
    '14000000_DisclosureOfAdditionalInformationAbstract': { display_name: 'Additional Information', category: 'Disclosure' },
    '21100000_StatementOfFinancialPositionAbstract': { display_name: 'SOFP CuNonCu', category: 'Statements' },
    '22000000_StatementOfProfitOrLossAbstract': { display_name: 'SOPL Nature', category: 'Statements' },
    '23000000_StatementOfCashFlowsAbstract': { display_name: 'SOCF', category: 'Statements' },
    '31000000_TradeAndOtherReceivablesAbstract': { display_name: 'Trade and Other Receivables', category: 'Notes' },
    '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract': { display_name: 'Property Plant Equipment', category: 'Notes' },
    '33000000_DisclosureOfIntangibleAssetsAbstract': { display_name: 'Intangible Assets', category: 'Notes' },
    '34000000_DisclosureOfRightofuseAssetsAbstract': { display_name: 'Right of use Assets', category: 'Notes' },
    '35000000_TradeAndOtherPayablesAbstract': { display_name: 'Trade and Other Payables', category: 'Notes' },
    '36000000_DisclosureOfLoansAndBorrowingsAbstract': { display_name: 'Loans and Borrowings', category: 'Notes' },
    '37000000_DisclosureOfRevenueAbstract': { display_name: 'Revenue', category: 'Notes' },
    '38000000_DisclosureOfSelectedIncomeExpensesAbstract': { display_name: 'Selected Income Expenses', category: 'Notes' },
    '39000000_RelatedPartyTransactionsAbstract': { display_name: 'Related Party', category: 'Notes' }
  };

  const sections = formStructureRef.current?.sections ? 
    Object.entries(formStructureRef.current.sections).map(([code, data]: [string, any]) => {
      const config = sectionConfig[code];
      return {
        code,
        name: config?.display_name || data.standard_label?.replace('[abstract]', '')?.trim() || code,
        category: config?.category || 'Other',
        data
      };
    }) : [];

  const companyInfo = companyInfoRef.current;
  const companyName = companyInfo?.company_name || 'Unknown Company';
  const yearEnd = companyInfo?.currentPeriod?.end || '';
  const formattedYearEnd = yearEnd ? 
    `${yearEnd.substring(0, 4)}-${yearEnd.substring(4, 6)}-${yearEnd.substring(6, 8)}` : '';

  const columns = buildColumns();

  // Collect members recursively (for SOCIE and Note tables)
  const collectMembers = (member: any, collected: any[] = []): any[] => {
    if (member.structure?.children) {
      member.structure.children.forEach((child: any) => {
        collectMembers(child, collected);
      });
    }
    
    // Exclude certain members
    if (member.name && 
        !['EquityMember', 'ClassesOfShareCapitalMember', 'EntitysTotalForRelatedPartiesMember', 'FundsAndReservesMember'].includes(member.name)) {
      if (!collected.find(m => m.name === member.name)) {
        collected.push(member);
      }
    }
    
    return collected;
  };

  // Build context for SOCIE/Note tables with member dimensions
  const getSOCIEContextRef = (
    item: any,
    periodOpening: string,
    periodStart: string,
    periodEnd: string,
    memberSuffix: string,
    memberName?: string,
    isOpening: boolean = false
  ): string => {
    const formattedOpening = periodOpening.replace(/-/g, '');
    const formattedStart = periodStart.replace(/-/g, '');
    const formattedEnd = periodEnd.replace(/-/g, '');
    
    const isRootMember = memberName && [
      'EquityMember',
      'FundsAndReservesMember',
      'PropertyPlantAndEquipmentMember',
      'IntangibleAssetsMember'
    ].includes(memberName);
    
    // Build base context
    let baseContext = '';
    
    if (isOpening) {
      baseContext = `asof_${formattedOpening}_${memberSuffix}`;
    } else if (item.properties?.period_type === 'duration') {
      baseContext = `fromto_${formattedStart}_${formattedEnd}_${memberSuffix}`;
    } else {
      baseContext = `asof_${formattedEnd}_${memberSuffix}`;
    }
    
    // Only append memberName if it exists and is not a root member
    if (memberName && !isRootMember) {
      return `${baseContext}_${memberName}`;
    }
    
    return baseContext;
  };

  // Render SOCIE table (matches 1.0 lines 855-1045)
  const renderSOCIETable = (sectionData: any, sectionCode: string) => {
    const footnoteRegistry = new Map<string, any>();

    const isSingaporePPESection =
      sectionCode === '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract' ||
      sectionCode === '33000000_DisclosureOfIntangibleAssetsAbstract';

    // SOCIE sections use a table wrapper at structure.children[0].
    // SG PPE/IA sections are direct axis+lineItems (same shape as PPETableSG).
    const tableContainer = isSingaporePPESection
      ? sectionData
      : (sectionData.structure?.children?.[0] || sectionData);
    const axis = tableContainer?.structure?.children?.[0];
    const lineItems = tableContainer?.structure?.children?.[1]?.structure?.children || [];
    
    // Collect members
    let allMembers: any[] = [];
    if (axis?.structure?.children?.[1]?.structure?.children?.[0]) {
      const root = axis.structure.children[1].structure.children[0];
      allMembers = collectMembers(root);
    }
    
    allMembers = allMembers.slice(0, 100);
    
    console.log('Rendering SOCIE/PPE view table:', {
      sectionCode,
      isSingaporePPESection,
      membersCount: allMembers.length,
      lineItemsCount: lineItems.length
    });
    
    if (allMembers.length === 0 || lineItems.length === 0) {
      return <p className="text-gray-500 p-4">No SOCIE data available</p>;
    }
    
    const companyInfo = companyInfoRef.current;
    
    // Render line item (recursive) - equityCounters passed per table
    const renderSOCIELineItem = (
      item: any,
      allMembers: any[],
      periodOpening: string,
      periodStart: string,
      periodEnd: string,
      memberSuffix: string,
      equityCounters: Record<string, number>,
      level: number = 0,
      itemIndex: number = 0
    ): JSX.Element | null => {
      if (item.properties?.abstract) {
        return (
          <React.Fragment key={item.name}>
            <tr>
              <td 
                className={`financial-header level-${level}`}
                style={{ paddingLeft: `${(level + 1) * 15}px` }}
                colSpan={allMembers.length + 2}
              >
                {item.standard_label?.replace('[abstract]', '')}
              </td>
            </tr>
            {item.structure?.children?.map((child: any, childIdx: number) => 
              renderSOCIELineItem(child, allMembers, periodOpening, periodStart, periodEnd, memberSuffix, equityCounters, level + 1, childIdx)
            )}
          </React.Fragment>
        );
      }
      
      // Track occurrences for opening/closing items (SOCIE + SG PPE/IA)
      const isEquityItem = [
        // SOCIE
        'Equity', 'EquityBalanceRestated', 'FundBalance',
        // PPE
        'GrossCarryingAmountOfPropertyPlantAndEquipment',
        'AccumulatedDepreciationAmortisationAndImpairmentOnPropertyPlantAndEquipment',
        'PropertyPlantAndEquipment',
        // Intangible assets
        'GrossCarryingAmountOfIntangibleAssets',
        'AmortisationAndImpairmentOnIntangibleAssetsOtherThanGoodwillGross',
        'IntangibleAssetsOtherThanGoodwill'
      ].includes(item.name);
      let occurrence = 1;
      
      if (isEquityItem) {
        const counterKey = `${item.name}_${memberSuffix}`;
        equityCounters[counterKey] = (equityCounters[counterKey] || 0) + 1;
        occurrence = equityCounters[counterKey];
      }
      
      const isOpening = isEquityItem && occurrence === 1;
      
      // Generate unique key (include index for uniqueness across tables)
      const itemKey = `${item.name}_${level}_${itemIndex}_${memberSuffix}`;
      
      const displayLabel =
        isSingaporePPESection && isEquityItem
          ? `${item.standard_label || ''}${isOpening ? ' (beginning of period)' : ' (closing of period)'}`
          : item.standard_label;

      return (
        <React.Fragment key={itemKey}>
          <tr>
            <td 
              className={`financial-item level-${level} ${negativeTargetsRef.current.includes(item.name) ? 'negative-indicator' : ''}`}
              style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
              title={negativeTargetsRef.current.includes(item.name) ? 'Subtracted from total' : ''}
            >
              {displayLabel}
            </td>
            
            {allMembers.map((member, idx) => {
              const contextRef = getSOCIEContextRef(item, periodOpening, periodStart, periodEnd, memberSuffix, member.name, isOpening);
              const value = getValue(item.qname, contextRef, footnoteRegistry);
              return (
                <td key={idx} className="text-right" dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
              );
            })}
            
            {/* Total column */}
            <td className="text-right">
              {(() => {
                const contextRef = getSOCIEContextRef(item, periodOpening, periodStart, periodEnd, memberSuffix, undefined, isOpening);
                const value = getValue(item.qname, contextRef, footnoteRegistry);
                return <span dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />;
              })()}
            </td>
          </tr>
          {item.structure?.children?.map((child: any, childIdx: number) =>
            renderSOCIELineItem(child, allMembers, periodOpening, periodStart, periodEnd, memberSuffix, equityCounters, level + 1, childIdx)
          )}
        </React.Fragment>
      );
    };
    
    // Render single table
    const renderSingleSOCIETable = (periodOpening: string, periodStart: string, periodEnd: string, memberSuffix: string, key: string) => {
      // Create NEW counter object for EACH table (matches 1.0 line 996)
      const equityCounters: Record<string, number> = {};
      
      return (
        <div key={key}>
          <h4 className="section-sub-header">
            Period: {periodStart} to {periodEnd}, {memberSuffix}
          </h4>
          <table className="view-table financial-table">
            <thead>
              <tr>
                <th style={{ width: '250px' }}>Components</th>
                {allMembers.map((member, idx) => (
                  <th key={idx}>{member.standard_label?.replace('[member]', '')}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item: any, itemIndex: number) => 
                renderSOCIELineItem(item, allMembers, periodOpening, periodStart, periodEnd, memberSuffix, equityCounters, 0, itemIndex)
              )}
            </tbody>
          </table>
        </div>
      );
    };
    
    const tables: JSX.Element[] = [];
    
    if (companyInfo.first_year === 'N') {
      if (companyInfo.consolidated === 'Y') {
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'ConsolidatedMember', 'cur_cons'));
        tables.push(renderSingleSOCIETable(companyInfo.previousPeriod.opening, companyInfo.previousPeriod.start, companyInfo.previousPeriod.end, 'ConsolidatedMember', 'prev_cons'));
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'SeparateMember', 'cur_sep'));
        tables.push(renderSingleSOCIETable(companyInfo.previousPeriod.opening, companyInfo.previousPeriod.start, companyInfo.previousPeriod.end, 'SeparateMember', 'prev_sep'));
      } else {
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'SeparateMember', 'cur_sep'));
        tables.push(renderSingleSOCIETable(companyInfo.previousPeriod.opening, companyInfo.previousPeriod.start, companyInfo.previousPeriod.end, 'SeparateMember', 'prev_sep'));
      }
    } else {
      if (companyInfo.consolidated === 'Y') {
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'ConsolidatedMember', 'cur_cons'));
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'SeparateMember', 'cur_sep'));
      } else {
        tables.push(renderSingleSOCIETable(companyInfo.currentPeriod.opening, companyInfo.currentPeriod.start, companyInfo.currentPeriod.end, 'SeparateMember', 'cur_sep'));
      }
    }
    
    return (
      <>
        {tables}
        {footnoteRegistry.size > 0 && (
          <div className="footnotes-section">
            <div className="footnotes-title">Footnotes</div>
            {Array.from(footnoteRegistry.entries())
              .sort((a, b) => a[1].number - b[1].number)
              .map(([key, data]) => (
                <div key={key} className="footnote-item">
                  <span className="footnote-number">[{data.number}]</span>
                  {data.text}
                </div>
              ))}
          </div>
        )}
      </>
    );
  };

  // Render Note tables (740000, 750000) - matches 1.0 lines 1046-1275
  const renderSingleNoteTable = (sectionData: any, sectionCode: string, memberSuffix: string, footnoteRegistry: Map<string, any>) => {
    // Note tables structure: index 0 = text block, index 1 = table
    const textBlock = sectionData.structure?.children?.[0];
    const table = sectionData.structure?.children?.[1];
    const axis = table?.structure?.children?.[0];
    const lineItems = table?.structure?.children?.[1]?.structure?.children || [];
    
    // Collect members
    let allMembers: any[] = [];
    if (axis?.structure?.children?.[1]?.structure?.children?.[0]) {
      const root = axis.structure.children[1].structure.children[0];
      allMembers = collectMembers(root);
    }
    
    allMembers = allMembers.slice(0, 100);
    
    console.log('Rendering Note table:', { memberSuffix, membersCount: allMembers.length, lineItemsCount: lineItems.length });
    
    if (allMembers.length === 0 || lineItems.length === 0) {
      return <p className="text-gray-500 p-4">No note table data available</p>;
    }
    
    const companyInfo = companyInfoRef.current;
    const sectionTitle = sectionCode.startsWith('740000') ? 'Share Capital' : 'Related Party Transactions';
    const periodOpening = companyInfo.currentPeriod.opening;
    const periodStart = companyInfo.currentPeriod.start;
    const periodEnd = companyInfo.currentPeriod.end;
    const shareCounters: Record<string, number> = {};
    
    // Render line item
    const renderNoteLineItem = (item: any, level: number = 0, itemIndex: number = 0): JSX.Element | null => {
      if (item.properties?.abstract) {
        return (
          <React.Fragment key={item.name}>
            <tr>
              <td 
                className={`financial-header level-${level}`}
                style={{ paddingLeft: `${(level + 1) * 15}px` }}
                colSpan={allMembers.length + 2}
              >
                {item.standard_label?.replace('[abstract]', '')}
              </td>
            </tr>
            {item.structure?.children?.map((child: any) => 
              renderNoteLineItem(child, level + 1)
            )}
          </React.Fragment>
        );
      }
      
      // Track occurrences for share fields
      const shareFields = ['AmountOfSharesOutstanding', 'NumberOfSharesOutstanding', 'AmountOfSharesIssuedAndFullyPaidOutstanding'];
      const isShareField = shareFields.includes(item.name);
      let occurrence = 1;
      
      if (isShareField) {
        const counterKey = `${item.name}_label`;
        shareCounters[counterKey] = (shareCounters[counterKey] || 0) + 1;
        occurrence = shareCounters[counterKey];
      }
      
      const isOpening = isShareField && occurrence === 1;
      const label = item.standard_label + (isShareField ? ` (${isOpening ? 'opening' : 'closing'})` : '');
      
      // Generate unique key (include index and period for uniqueness)
      const itemKey = `${item.name}_${level}_${itemIndex}_${memberSuffix}_${isOpening ? 'open' : 'close'}`;
      
      return (
        <React.Fragment key={itemKey}>
          <tr>
            <td 
              className={`financial-item level-${level}`}
              style={{ paddingLeft: `${(level + 1) * 15}px` }}
            >
              {negativeTargetsRef.current.includes(item.name) ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>{label}</span>
                  <span className="tooltip-icon" style={{ position: 'relative', display: 'inline-block', cursor: 'help' }}>
                    <span style={{ color: '#dc3545', fontSize: '20px' }}>▼</span>
                    <span className="tooltip-text" style={{
                      visibility: 'hidden',
                      position: 'absolute',
                      bottom: '125%',
                      right: '0',
                      backgroundColor: '#333',
                      color: '#fff',
                      textAlign: 'center',
                      borderRadius: '6px',
                      padding: '5px 10px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      zIndex: 1
                    }}>
                      Subtracted from total
                    </span>
                  </span>
                </div>
              ) : (
                label
              )}
            </td>
            
            {allMembers.map((member, idx) => {
              const contextRef = getSOCIEContextRef(item, periodOpening, periodStart, periodEnd, memberSuffix, member.name, isOpening);
              const value = getValue(item.qname, contextRef, footnoteRegistry);
              return (
                <td key={idx} className="text-right" dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
              );
            })}
            
            {/* Total column */}
            <td className="text-right">
              {(() => {
                const contextRef = getSOCIEContextRef(item, periodOpening, periodStart, periodEnd, memberSuffix, undefined, isOpening);
                const value = getValue(item.qname, contextRef, footnoteRegistry);
                return <span dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />;
              })()}
            </td>
          </tr>
          {item.structure?.children?.map((child: any, childIdx: number) =>
            renderNoteLineItem(child, level + 1, childIdx)
          )}
        </React.Fragment>
      );
    };
    
    return (
      <>
        <h4 className="section-sub-header">
          {sectionTitle}: {periodStart} to {periodEnd}, {memberSuffix}
        </h4>
        <table className="view-table financial-table">
          <thead>
            <tr>
              <th style={{ width: '250px' }}>Components</th>
              {allMembers.map((member, idx) => (
                <th key={idx}>{member.standard_label?.replace('[member]', '')}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item: any, itemIndex: number) => renderNoteLineItem(item, 0, itemIndex))}
          </tbody>
        </table>
      </>
    );
  };

  // Wrapper function to render Note tables for all member types
  const renderNoteTable = (sectionData: any, sectionCode: string) => {
    const footnoteRegistry = new Map<string, any>();
    const companyInfo = companyInfoRef.current;
    const tables: JSX.Element[] = [];
    
    // Render text block ONCE at the top (matches 1.0 lines 1050-1075)
    const textBlock = sectionData.structure?.children?.[0];
    let textBlockElement: JSX.Element | null = null;
    
    if (textBlock && textBlock.qname && textBlock.properties?.data_type === 'text') {
      const contextRef = getSimpleContextRef(textBlock);
      const textValue = getValue(textBlock.qname, contextRef);
      
      if (textValue && textValue.trim()) {
        textBlockElement = (
          <div className="text-block-container mb-6" key={`${sectionCode}_textblock`}>
            <h4 className="section-sub-header">
              {textBlock.standard_label?.replace('[text block]', '').replace('[textblock]', '').trim()}
            </h4>
            <div className="border p-4" dangerouslySetInnerHTML={{ __html: textValue }} />
          </div>
        );
      }
    }
    
    // Render for current period only (Note tables don't have previous period)
    if (companyInfo.consolidated === 'Y') {
      // Render ConsolidatedMember table
      tables.push(
        <React.Fragment key={`${sectionCode}_cons`}>
          {renderSingleNoteTable(sectionData, sectionCode, 'ConsolidatedMember', footnoteRegistry)}
        </React.Fragment>
      );
      
      // Render SeparateMember table
      tables.push(
        <React.Fragment key={`${sectionCode}_sep`}>
          {renderSingleNoteTable(sectionData, sectionCode, 'SeparateMember', footnoteRegistry)}
        </React.Fragment>
      );
    } else {
      // Only SeparateMember
      tables.push(
        <React.Fragment key={`${sectionCode}_sep`}>
          {renderSingleNoteTable(sectionData, sectionCode, 'SeparateMember', footnoteRegistry)}
        </React.Fragment>
      );
    }
    
    return (
      <>
        {textBlockElement}
        {tables}
        {footnoteRegistry.size > 0 && (
          <div className="footnotes-section">
            <div className="footnotes-title">Footnotes</div>
            {Array.from(footnoteRegistry.entries())
              .sort((a, b) => a[1].number - b[1].number)
              .map(([key, data]) => (
                <div key={key} className="footnote-item">
                  <span className="footnote-number">[{data.number}]</span>
                  {data.text}
                </div>
              ))}
          </div>
        )}
      </>
    );
  };

  // Find line items (matches 1.0 logic lines 628-662)
  const findLineItems = (sectionData: any, sectionCode: string): any[] => {
    let lineItems: any[] = [];
    
    if (!sectionData.structure?.children) return [];
    
    // Special handling for SORE (620000) - navigate deeper to skip headers (matches 1.0 lines 613-625)
    if (sectionCode.startsWith('620000')) {
      try {
        const path = sectionData.structure.children?.[0]?.structure?.children?.[0]?.structure?.children?.[1]?.structure?.children;
        if (path && path.length > 0) {
          console.log('Found SORE LineItems via deep navigation (620000 special case)');
          return path;
        }
      } catch (e) {
        console.warn('SORE deep navigation failed, using fallback');
      }
    }
    
    // Method 1: Find field with name ending in "LineItems"
    for (const field of sectionData.structure.children) {
      if (field.name?.endsWith('LineItems') && field.structure?.children) {
        console.log('Found LineItems by name:', field.name);
        return field.structure.children;
      }
    }
    
    // Method 2: Look in table structure for "LineItems"
    for (const field of sectionData.structure.children) {
      if (field.structure?.children) {
        for (const child of field.structure.children) {
          if (child.name?.endsWith('LineItems') && child.structure?.children) {
            console.log('Found LineItems in table structure:', child.name);
            return child.structure.children;
          }
        }
      }
    }
    
    // Method 3: Look for specific abstract containers
    for (const field of sectionData.structure.children) {
      if (['AssetsAbstract', 'EquityAndLiabilitiesAbstract', 'RevenueAbstract'].includes(field.name)) {
        console.log('Found by abstract container:', field.name);
        return sectionData.structure.children;
      }
    }
    
    // Fallback: use all children
    console.warn('Using fallback: all children');
    return sectionData.structure.children;
  };

  // Render financial table (SOFP, SOPL, etc.)
  const renderFinancialTable = (sectionData: any, sectionCode: string) => {
    const footnoteRegistry = new Map<string, any>();
    const counters: Record<string, number> = {}; // For occurrence tracking
    const isRetainedEarningsSection = sectionCode.startsWith('620000');
    
    // Navigate to line items (matches 1.0 taxonomy_viewer lines 612-662)
    const lineItems = findLineItems(sectionData, sectionCode);
    
    console.log(`Rendering financial table ${sectionCode}:`, {
      lineItemsCount: lineItems.length,
      columnsCount: columns.length,
      firstItem: lineItems[0]?.name
    });
    
    const renderLineItem = (item: any, level: number = 0, itemIndex: number = 0): JSX.Element | null => {
      if (item.properties?.abstract) {
        return (
          <React.Fragment key={item.name}>
            <tr>
              <td 
                className={`financial-header level-${level}`}
                colSpan={columns.length + 1}
                style={{ paddingLeft: `${(level + 1) * 15}px` }}
              >
                {item.standard_label?.replace('[abstract]', '')}
              </td>
            </tr>
            {item.structure?.children?.map((child: any, childIdx: number) => renderLineItem(child, level + 1, childIdx))}
          </React.Fragment>
        );
      }
      
      // Handle special occurrence tracking (matches 1.0 lines 780-826)
      const needsOccurrenceTracking = item.name === 'CashAndCashEquivalents' ||
                                       (item.name === 'RetainedEarnings' && isRetainedEarningsSection) ||
                                       item.name === 'RetainedEarningsRestated';
      
      // Update display label for Cash
      let displayLabel = item.standard_label;
      if (item.name === 'CashAndCashEquivalents') {
        const labelKey = 'CashAndCashEquivalents_label';
        counters[labelKey] = (counters[labelKey] || 0) + 1;
        displayLabel = counters[labelKey] === 1 ? 
          'Cash and cash equivalents at beginning of period' : 
          item.standard_label;
      } else if (item.name === 'RetainedEarnings' && isRetainedEarningsSection) {
        const labelKey = 'RetainedEarnings_label';
        counters[labelKey] = (counters[labelKey] || 0) + 1;
        displayLabel = counters[labelKey] === 1 ? 
          'Retained earnings at beginning of period' :
          counters[labelKey] === 2 ?
          'Retained earnings at end of period' :
          item.standard_label;
      }
      
      return (
        <React.Fragment key={`${item.name}_${level}_${itemIndex}`}>
          <tr>
            <td 
              className={`financial-item level-${level} ${negativeTargetsRef.current.includes(item.name) ? 'negative-indicator' : ''}`}
              style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
              title={negativeTargetsRef.current.includes(item.name) ? 'Subtracted from total' : ''}
            >
              {displayLabel}
            </td>
            {columns.map((column, idx) => {
              let contextRef: string;
              
              // Check if needs occurrence-based context (matches SOFPTable.tsx lines 560-632)
              if (needsOccurrenceTracking) {
                const counterKey = `${item.name}_${column.period}_${column.member}`;
                counters[counterKey] = (counters[counterKey] || 0) + 1;
                const occurrence = counters[counterKey];
                
                const companyInfo = companyInfoRef.current;
                const period = column.period === 'current' ? companyInfo.currentPeriod : companyInfo.previousPeriod;
                const cleanMember = column.member.startsWith('_') ? column.member.substring(1) : column.member;
                
                if (occurrence === 1) {
                  // First occurrence = opening period
                  contextRef = `asof_${period.opening.replace(/-/g, '')}_${cleanMember}`;
                } else {
                  // Second occurrence = closing period
                  contextRef = `asof_${period.end.replace(/-/g, '')}_${cleanMember}`;
                }
              } else {
                // Normal context building
                contextRef = getContextRef(item, column);
              }
              
              const value = getValue(item.qname, contextRef, footnoteRegistry);
              
              return (
                <td key={idx} className="text-right" dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
              );
            })}
          </tr>
          {/* Recurse for children */}
          {item.structure?.children && item.structure.children.map((child: any, childIdx: number) => renderLineItem(child, level + 1, childIdx))}
        </React.Fragment>
      );
    };

    if (lineItems.length === 0) {
      return <p className="text-gray-500 p-4">No line items found in this section</p>;
    }

    return (
      <>
        <table className="view-table financial-table">
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Item</th>
              {columns.map((col, idx) => (
                <th key={idx}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item: any, itemIndex: number) => renderLineItem(item, 0, itemIndex))}
          </tbody>
        </table>
        
        {footnoteRegistry.size > 0 && (
          <div className="footnotes-section">
            <div className="footnotes-title">Footnotes</div>
            {Array.from(footnoteRegistry.entries())
              .sort((a, b) => a[1].number - b[1].number)
              .map(([key, data]) => (
                <div key={key} className="footnote-item">
                  <span className="footnote-number">[{data.number}]</span>
                  {data.text}
                </div>
              ))}
          </div>
        )}
      </>
    );
  };

  // Get context for simple fields (no member dimension)
  const getSimpleContextRef = (field: any): string => {
    const companyInfo = companyInfoRef.current;
    if (!companyInfo) return '';
    
    const period = companyInfo.currentPeriod;
    const periodType = field.properties?.period_type;
    
    if (periodType === 'duration') {
      return `fromto_${period.start.replace(/-/g, '')}_${period.end.replace(/-/g, '')}`;
    } else {
      return `asof_${period.end.replace(/-/g, '')}`;
    }
  };

  // Render standard fields (returns mixed table rows and blocks)
  // For Note - Accounting Policy (720000) / Note - Other (730000), can sort text blocks by report note no.
  const renderStandardSection = (fields: any[], sectionCode?: string): JSX.Element[] => {
    const useReportOrder =
      sortByNoteNo && !!sectionCode && isNotePolicyOrOtherSection(sectionCode);

    // Report order: flatten text blocks and sort by parsed note/policy ref (6, 5.4, (a)…)
    if (useReportOrder) {
      type TextBlockItem = {
        field: any;
        value: string;
        originalIndex: number;
        label: string | null;
      };

      const collectTextBlocks = (
        fieldsList: any[],
        out: TextBlockItem[] = [],
        counter: { i: number } = { i: 0 }
      ): TextBlockItem[] => {
        fieldsList.forEach((field) => {
          if (!field) return;
          if (field.properties?.data_type === 'text' && field.qname) {
            const contextRef = getSimpleContextRef(field);
            const value = getValue(field.qname, contextRef);
            if (value && String(value).trim()) {
              out.push({
                field,
                value: String(value),
                originalIndex: counter.i++,
                label: extractReportNoteLabel(String(value)),
              });
            }
          }
          if (
            field.structure?.children &&
            field.name !== 'DetailsOfPrincipalActivitiesOfBusinessAbstract' &&
            field.name !== 'AuditorsInformationTable' &&
            field.name !== 'AuditorsInformationLineitem' &&
            field.name !== 'DisclosureOfCompaniesLimitedByGuaranteeAbstract'
          ) {
            collectTextBlocks(field.structure.children, out, counter);
          }
        });
        return out;
      };

      const blocks = collectTextBlocks(fields);
      blocks.sort((a, b) => {
        const cmp = compareReportNoteSortKeys(
          getReportNoteSortKeyFromHtml(a.value),
          getReportNoteSortKeyFromHtml(b.value)
        );
        if (cmp !== 0) return cmp;
        return a.originalIndex - b.originalIndex;
      });

      return blocks.map(({ field, value, originalIndex, label }) => (
        <div
          key={`text_report_${field.name}_${originalIndex}`}
          id={`${sectionCode}__block_${field.name}_${originalIndex}`}
          className="text-block-container"
          data-report-note={label || undefined}
        >
          <h4 className="section-sub-header">
            {label ? <span className="report-note-badge">{label}</span> : null}
            {field.standard_label?.replace('[text block]', '').replace('[textblock]', '').trim()}
          </h4>
          <div className="border p-4" dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      ));
    }

    const elements: JSX.Element[] = [];
    let currentTableRows: JSX.Element[] = [];
    let tableKey = 0;
    
    const flushTable = () => {
      if (currentTableRows.length > 0) {
        elements.push(
          <table key={`table_${tableKey++}`} className="view-table mb-4">
            <tbody>{currentTableRows}</tbody>
          </table>
        );
        currentTableRows = [];
      }
    };
    
    const processFields = (fieldsList: any[], level: number = 0) => {
      fieldsList.forEach((field, index) => {
        if (!field) return;
        
        // Business table (matches 1.0 lines 1284-1333)
        if (field.name === 'DetailsOfPrincipalActivitiesOfBusinessAbstract') {
          flushTable();
          
          const table = field.structure?.children?.[0];
          const axis = table?.structure?.children?.[0];
          const members = axis?.structure?.children?.[0]?.structure?.children || [];
          const lineItems = field.structure?.children?.[1]?.structure?.children || [];
          
          elements.push(
            <div key={`business_${index}`}>
              <h4 className="section-sub-header">Principal activities of business</h4>
              <table className="view-table financial-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>MSIC Code</th>
                    <th>Description of business</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member: any, mIdx: number) => (
                    <tr key={mIdx}>
                      <td>Business {mIdx + 1}</td>
                      {lineItems.slice(0, 2).map((item: any, iIdx: number) => {
                        if (item.properties?.abstract || !item.qname) return null;
                        
                        const companyInfo = companyInfoRef.current;
                        const period = companyInfo.currentPeriod;
                        
                        let contextKey: string;
                        if (item.properties?.period_type === 'duration') {
                          contextKey = `fromto_${period.start.replace(/-/g, '')}_${period.end.replace(/-/g, '')}_${member.name}`;
                        } else {
                          contextKey = `asof_${period.end.replace(/-/g, '')}_${member.name}`;
                        }
                        
                        const value = getValue(item.qname, contextKey);
                        return (
                          <td key={iIdx} dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          return;
        }
        
        // CLBG table (matches 1.0 lines 1335-1384)
        if (field.name === 'DisclosureOfCompaniesLimitedByGuaranteeAbstract') {
          flushTable();
          
          const table = field.structure?.children?.[0]?.structure?.children?.[0];
          const axis = table?.structure?.children?.[0];
          const members = axis?.structure?.children?.[0]?.structure?.children || [];
          const lineItems = field.structure?.children?.[0]?.structure?.children?.[1]?.structure?.children || [];
          
          elements.push(
            <div key={`clbg_${index}`}>
              <h4 className="section-sub-header">Category of object of companies limited by guarantee</h4>
              <table className="view-table financial-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Disclosure on categories of objects of companies limited by guarantee</th>
                    <th>Disclosure on sub-categories of object of companies limited by guarantee</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member: any, mIdx: number) => (
                    <tr key={mIdx}>
                      <td>Object {mIdx + 1}</td>
                      {lineItems.slice(0, 2).map((item: any, iIdx: number) => {
                        if (item.properties?.abstract || !item.qname) return null;
                        
                        const companyInfo = companyInfoRef.current;
                        const period = companyInfo.currentPeriod;
                        
                        let contextKey: string;
                        if (item.properties?.period_type === 'duration') {
                          contextKey = `fromto_${period.start.replace(/-/g, '')}_${period.end.replace(/-/g, '')}_${member.name}`;
                        } else {
                          contextKey = `asof_${period.end.replace(/-/g, '')}_${member.name}`;
                        }
                        
                        const value = getValue(item.qname, contextKey);
                        return (
                          <td key={iIdx} dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          return;
        }
        
        // Auditors Information (uses special _AuditorsCountAxis_0001 context)
        if (field.name === 'AuditorsInformationTable' || field.name === 'AuditorsInformationLineitem') {
          flushTable();
          
          // Render auditor fields with special context - RECURSIVE to handle nested abstracts
          const renderAuditorFields = (children: any[], keyPrefix: string = ''): React.ReactNode[] => {
            const results: React.ReactNode[] = [];
            
            children.forEach((child: any, cIdx: number) => {
              const childKey = `${keyPrefix}_${cIdx}`;
              
              if (child.properties?.abstract && child.structure?.children) {
                // Recurse into abstract's children
                results.push(...renderAuditorFields(child.structure.children, childKey));
              } else if (!child.properties?.abstract) {
                // Render non-abstract field
                const companyInfo = companyInfoRef.current;
                const period = companyInfo.currentPeriod;
                
                // Build context with AuditorsCountAxis
                let contextRef: string;
                if (child.properties?.period_type === 'duration') {
                  contextRef = `fromto_${period.start.replace(/-/g, '')}_${period.end.replace(/-/g, '')}_AuditorsCountAxis_0001`;
                } else {
                  contextRef = `asof_${period.end.replace(/-/g, '')}_AuditorsCountAxis_0001`;
                }
                
                const value = getValue(child.qname, contextRef);
                
                results.push(
                  <tr key={`auditor${childKey}`}>
                    <th>{child.standard_label}</th>
                    <td dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
                  </tr>
                );
              }
            });
            
            return results;
          };
          
          elements.push(
            <table key={`auditor_${index}`} className="view-table mb-4">
              <tbody>
                {field.structure?.children && renderAuditorFields(field.structure.children)}
              </tbody>
            </table>
          );
          return;
        }
        
        // Text block fields - break out of table
        if (field.properties?.data_type === 'text' && field.qname) {
          flushTable();
          
          const contextRef = getSimpleContextRef(field);
          const value = getValue(field.qname, contextRef);
          if (value) {
            elements.push(
              <div
                key={`text_${field.name}_${index}`}
                id={sectionCode ? `${sectionCode}__block_${field.name}_${index}` : undefined}
                className="text-block-container"
              >
                <h4 className="section-sub-header">
                  {field.standard_label?.replace('[text block]', '').replace('[textblock]', '').trim()}
                </h4>
                <div 
                  className="border p-4"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              </div>
            );
          }
        }
        // Simple fields - add to current table
        else if (!field.properties?.abstract && field.qname) {
          const contextRef = getSimpleContextRef(field);
          const value = getValue(field.qname, contextRef);
          currentTableRows.push(
            <tr key={`${field.name}_${index}`}>
              <th className={`level-${level}`}>{field.standard_label}</th>
              <td dangerouslySetInnerHTML={{ __html: value || '&nbsp;' }} />
            </tr>
          );
        }
        
        // Recurse for children (skip business and auditor tables)
        if (field.structure?.children && 
            field.name !== 'DetailsOfPrincipalActivitiesOfBusinessAbstract' &&
            field.name !== 'AuditorsInformationTable' &&
            field.name !== 'AuditorsInformationLineitem') {
          processFields(field.structure.children, level + 1);
        }
      });
    };
    
    processFields(fields);
    flushTable(); // Flush any remaining rows
    
    return elements;
  };

  return (
    <div className="view-filing-page">
      {/* Full-width top bar — T crossbar when Review is open */}
      <div className="main-panel view-top-bar no-print">
        <div className="panel-item panel-item-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0f172a'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
            >
              All Filings
            </button>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '500' }}>{companyName}</span>
              {formattedYearEnd && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.25rem 0.5rem',
                  fontSize: '12px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe'
                }}>
                  {formattedYearEnd}
                </span>
              )}
            </div>
            {/* Compact section nav while PDF side-by-side is open */}
            <div
              ref={compactNavRef}
              className="view-compact-section-nav"
              style={{ display: 'none' }}
            >
              <select
                value={activeSectionId}
                onChange={(e) => scrollToSection(e.target.value)}
                className="view-compact-section-select"
                title="Jump to section"
              >
                {sections.map((section) => (
                  <option key={section.code} value={section.code}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="panel-item panel-item-right">
          <div className="panel-actions">
            <button
              type="button"
              className="action-btn action-btn-secondary"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              title="Refresh data from database"
            >
              {refreshing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh</span>
                </>
              )}
            </button>

            <button
              type="button"
              className={`action-btn action-btn-secondary ${sortByNoteNo ? 'is-active' : ''}`}
              onClick={() => setSortByNoteNo((v) => !v)}
              title="ON: sort Accounting Policy and Note - Other by note sequence (1, 2, 3…). OFF: keep taxonomy order."
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-3-3m3 3l3-3" />
              </svg>
              <span>Note</span>
              <span className={`view-sort-note-state ${sortByNoteNo ? 'is-on' : 'is-off'}`}>
                {sortByNoteNo ? 'ON' : 'OFF'}
              </span>
            </button>

            <button
              type="button"
              className="action-btn action-btn-secondary"
              onClick={handleComparePDF}
              title="Review with uploaded PDF"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              <span>Review</span>
            </button>

            <button
              type="button"
              className="action-btn action-btn-secondary"
              onClick={handlePrint}
              disabled={generatingPDF}
              title="Download PDF"
            >
              {generatingPDF ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span>Print PDF</span>
                </>
              )}
            </button>

            <button
              type="button"
              className="action-btn action-btn-primary"
              onClick={() => navigate(-1)}
              title="Back to previous page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Filings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Body: sidebar + content (+ PDF column) under the full-width header */}
      <div ref={mainLayoutRef} className="view-main-layout">
        {/* Left: Sidebar Navigation (hidden when PDF open) */}
        <div ref={sidebarRef} className="view-sidebar no-print">
          <nav>
            {(() => {
              let currentCategory = '';
              return sections.map((section) => {
                const categoryHeader = section.category !== currentCategory ? (
                  <div key={`cat_${section.category}`} className="view-nav-category">
                    <h4>{section.category}</h4>
                  </div>
                ) : null;
                
                currentCategory = section.category;
                
                return (
                  <React.Fragment key={section.code}>
                    {categoryHeader}
                    <button
                      onClick={() => scrollToSection(section.code)}
                      className={`view-nav-item ${activeSectionId === section.code ? 'active' : ''}`}
                    >
                      {section.name}
                    </button>
                  </React.Fragment>
                );
              });
            })()}
          </nav>
        </div>

        {/* Middle: Content (60% when PDF open) */}
        <div ref={rightAreaRef} className="view-right-area">
          <div className="view-content" ref={scrollContainerRef}>
            {sections.map((section, index) => {
              const sectionData = section.data;
              const sectionCode = section.code;
              
              // Determine section type
              const isFinancialSection = sectionCode.startsWith('200100') || // SOFP
                                        sectionCode.startsWith('200200') || // SOFP Details
                                        sectionCode.startsWith('300100') || // SOPL/SOIE
                                        sectionCode.startsWith('300200') || // SOPL/SOIE Details
                                        sectionCode.startsWith('400100') || // SOCI
                                        sectionCode.startsWith('500100') || // Cash Flow
                                        sectionCode.startsWith('620000') || // SORE
                                        sectionCode.startsWith('210000') || // SOFP CuNonCu
                                        sectionCode.startsWith('220000') || // SOFP Liquidity
                                        sectionCode.startsWith('310000') || // SOPL
                                        // Singapore ACRA financial sections
                                        sectionCode === '21100000_StatementOfFinancialPositionAbstract' ||
                                        sectionCode === '22000000_StatementOfProfitOrLossAbstract' ||
                                        sectionCode === '23000000_StatementOfCashFlowsAbstract' ||
                                        sectionCode === '31000000_TradeAndOtherReceivablesAbstract' ||
                                        sectionCode === '34000000_DisclosureOfRightofuseAssetsAbstract' ||
                                        sectionCode === '35000000_TradeAndOtherPayablesAbstract' ||
                                        sectionCode === '36000000_DisclosureOfLoansAndBorrowingsAbstract' ||
                                        sectionCode === '37000000_DisclosureOfRevenueAbstract' ||
                                        sectionCode === '38000000_DisclosureOfSelectedIncomeExpensesAbstract' ||
                                        sectionCode === '39000000_RelatedPartyTransactionsAbstract';
              
              const isSOCIESection = sectionCode.startsWith('610000_'); // SOCIE
              const isSingaporePPESection =
                sectionCode === '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract' ||
                sectionCode === '33000000_DisclosureOfIntangibleAssetsAbstract';
              const isNoteTableSection = sectionCode.startsWith('740000_') || sectionCode.startsWith('750000_'); // Note tables
              
              // Do not pre-mark landscape. After empty columns are hidden, a
              // useEffect adds .section-landscape only when visible cols >= 8.
              // 720000/730000 stay portrait at section level; each disclosure
              // text-block is classified on its own.
              const isWideTable = isSOCIESection || isSingaporePPESection || isNoteTableSection
              const isFitWidth = isSingaporePPESection;
              const needsPageBreakElement = index > 0;
              
              return (
                <React.Fragment key={section.code}>
                  {/* Page break BETWEEN portrait sections only */}
                  {needsPageBreakElement && <div className="page-break" />}
                  <div
                    id={section.code}
                    className={`view-section${isWideTable ? ' section-wide-table' : ''}${isFitWidth ? ' section-fit-width' : ''}`}
                  >
                    <div className="section-header-view-filing">{section.name}</div>
                  
                    {isFinancialSection ? (
                      renderFinancialTable(sectionData, sectionCode)
                    ) : isSOCIESection ? (
                      renderSOCIETable(sectionData, sectionCode)
                    ) : isSingaporePPESection ? (
                      renderSOCIETable(sectionData, sectionCode)
                    ) : isNoteTableSection ? (
                      renderNoteTable(sectionData, sectionCode)
                    ) : (
                      <>
                        {sectionData.structure?.children &&
                          renderStandardSection(sectionData.structure.children, sectionCode)}
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Right: PDF Viewer (always mounted; CSS-hidden when closed — same width as EditFilings) */}
        <div ref={pdfColumnRef} className="pdf-viewer-hidden-container no-print">
          <PDFViewer
            isOpen={true}
            onClose={() => setPdfViewerOpen(false)}
            orgId={filingOrgId || undefined}
            filingId={filingId}
          />
        </div>
      </div>
    </div>
  );
}
